import { useState } from "preact/hooks";
import type { DashboardViewModel } from "../app/dashboardViewModel";
import { formatGrind } from "../app/grindFormat";
import { isoDate } from "../engine/dates";
import type { Bean, Shot } from "../engine/model";
import type { Repositories } from "../storage/repositories";
import { AddBeanDialog } from "./AddBeanDialog";
import { BackupDialog } from "./BackupDialog";
import { BeansDialog } from "./BeansDialog";
import { ActionSheet, ConfirmDialog, type ConfirmState, type SheetAction } from "./dialogs";
import { GrindersDialog } from "./GrindersDialog";
import { FirstRun } from "./FirstRun";
import { InstallHint } from "./InstallHint";
import { LogShotForm, LOG_FORM_ID, TIME_INPUT_ID } from "./LogShotForm";
import { PredictionCard } from "./PredictionCard";
import { SettingsDialog } from "./SettingsDialog";
import { ShotEditDialog } from "./ShotEditDialog";
import { ShotHistory } from "./ShotHistory";
import { updateAvailable } from "./updatePrompt";

function beanLabel(b: Bean): string {
  return `${b.name}${b.roaster ? ` · ${b.roaster}` : ""}${b.freezeDate ? " ❄" : ""}`;
}

/**
 * One-handed layout, no top bar: install banner, bean picker first with a meta line whose ⋯ is the single app menu
 * (Add / Edit / Archive bean, then Settings), recommendation card with grinder chip and Log shot, form, history.
 */
export function App({ vm, repos }: { vm: DashboardViewModel; repos: Repositories }) {
  const [beanDialog, setBeanDialog] = useState<{ open: boolean; initial: Bean | null }>({ open: false, initial: null });
  const [beansOpen, setBeansOpen] = useState(false);
  const [backup, setBackup] = useState(false);
  const [grindersOpen, setGrindersOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [sheet, setSheet] = useState<{ title: string; actions: SheetAction[] } | null>(null);
  const [editingShot, setEditingShot] = useState<Shot | null>(null);

  const grinders = vm.grinders.value;
  const beans = vm.beans.value;
  const g = vm.selectedGrinder.value;
  const b = vm.selectedBean.value;
  const update = updateAvailable.value;

  const askArchive = () => {
    const bean = vm.selectedBean.value;
    if (!bean) return;
    setConfirm({
      title: "Archive bean",
      message: `Hide "${bean.name}" from the pantry? Shot history is kept.`,
      confirmLabel: "Archive",
      onConfirm: () => void vm.archiveSelectedBean(),
    });
  };

  const addBean = () => setBeanDialog({ open: true, initial: null });

  const openSettings = () => { setSettingsOpen(true); void vm.refreshBackupReminder(); };

  /** The one app menu (the ⋯ on the bean meta row). "All beans" lives in Settings → Beans. */
  const appMenu = () => {
    const bean = vm.selectedBean.value;
    setSheet({
      title: bean ? bean.name : "Menu",
      actions: [
        { label: "Add bean", onSelect: addBean },
        ...(bean ? [
          { label: "Edit bean", onSelect: () => setBeanDialog({ open: true, initial: bean }) },
          ...(vm.nudgeOffset.value !== 0 && bean.targetTimeS === null
            ? [{ label: `Undo target nudge (${vm.nudgeOffset.value > 0 ? "+" : ""}${vm.nudgeOffset.value} s)`, onSelect: () => void vm.undoNudge() }] : []),
          { label: "Archive bean", onSelect: askArchive },
        ] : []),
        { label: vm.backup.value.due ? "Settings · backup due" : "Settings", dividerBefore: true, onSelect: openSettings },
      ],
    });
  };

  const beanPicker = () => {
    if (beans.length === 0) { addBean(); return; }
    setSheet({
      title: "Choose bean",
      actions: [
        ...beans.map((x) => ({ label: `${x.id === b?.id ? "✓ " : ""}${beanLabel(x)}`, onSelect: () => void vm.selectBean(x) })),
        { label: "Add bean", onSelect: addBean },
      ],
    });
  };

  const grinderPicker = () => {
    setSheet({
      title: "Choose grinder",
      actions: grinders.map((x) => ({ label: `${x.id === g?.id ? "✓ " : ""}${x.name}`, onSelect: () => void vm.selectGrinder(x) })),
    });
  };

  const logShot = () => {
    // Focus synchronously inside the tap so iOS opens the decimal keypad, then scroll the form into view.
    const time = document.getElementById(TIME_INPUT_ID) as HTMLInputElement | null;
    time?.focus({ preventScroll: true });
    document.getElementById(LOG_FORM_ID)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const shotMenu = (s: Shot) => {
    const grind = g ? formatGrind(s.grindSetting, g) : String(s.grindSetting);
    setSheet({
      title: `${s.timeSeconds.toFixed(1)} s at grind ${grind}`,
      actions: [
        { label: "Edit shot", onSelect: () => setEditingShot(s) },
        { label: "Delete shot", danger: true, onSelect: () => setConfirm({
          title: "Delete shot",
          message: `Delete the ${s.timeSeconds.toFixed(1)} s shot at grind ${grind}? The prediction will be recomputed.`,
          confirmLabel: "Delete", danger: true, onConfirm: () => void vm.deleteShot(s),
        }) },
      ],
    });
  };

  const d = new Date();
  const today = isoDate(d.getFullYear(), d.getMonth() + 1, d.getDate());

  return (
    <>
      <h1 class="sr-only">Puck Tech</h1>

      <InstallHint vm={vm} />
      {vm.firstRunVisible.value ? (
        <FirstRun vm={vm} onPickGrinder={grinderPicker} onAddGrinder={() => setGrindersOpen(true)} onAddBean={addBean} />
      ) : (<>
      <section class="bean-block" aria-label="Bean">
        <button type="button" class="bean-picker" aria-haspopup="dialog" onClick={beanPicker}>
          <span class="name">{b ? beanLabel(b) : "No beans yet"}</span>
          <span class="caret" aria-hidden="true">{"▾"}</span>
        </button>
        <div class="bean-meta">
          <span class="hint grow">{vm.beanMeta.value}</span>
          <button class="more has-dot" type="button" aria-haspopup="dialog"
                  aria-label={vm.backup.value.due ? "Menu, backup due" : "Menu"} onClick={appMenu}>
            {"⋯"}{vm.backup.value.due && <span class="dot" aria-hidden="true" />}
          </button>
        </div>
        {beans.length === 0 && (
          <button class="pill-dark add-first-bean" type="button" onClick={addBean}>Add bean</button>
        )}
      </section>

      <PredictionCard vm={vm} onGrinderChip={grinderPicker} onLogShot={logShot} />
      <LogShotForm vm={vm} />
      <ShotHistory vm={vm} onMenu={shotMenu} />
      </>)}

      <AddBeanDialog open={beanDialog.open} initial={beanDialog.initial} today={today}
                     onClose={() => setBeanDialog({ open: false, initial: null })}
                     onSave={(bean) => { setBeanDialog({ open: false, initial: null }); void (bean.id === null ? vm.addBean(bean) : vm.updateBean(bean)); }} />
      <BeansDialog open={beansOpen} vm={vm} onClose={() => setBeansOpen(false)}
                   onEdit={(bean) => { setBeansOpen(false); setBeanDialog({ open: true, initial: bean }); }} />
      <ShotEditDialog shot={editingShot} grinder={g} onClose={() => setEditingShot(null)} onSave={(s) => vm.updateShot(s)} />
      <BackupDialog open={backup} repos={repos} vm={vm} onClose={() => setBackup(false)} />
      <GrindersDialog open={grindersOpen} vm={vm} onClose={() => setGrindersOpen(false)} />
      <SettingsDialog open={settingsOpen} vm={vm} onClose={() => setSettingsOpen(false)}
                      onOpenBeans={() => setBeansOpen(true)} onOpenGrinders={() => setGrindersOpen(true)} onOpenBackup={() => setBackup(true)} />
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
      <ActionSheet title={sheet?.title ?? null} actions={sheet?.actions ?? []} onClose={() => setSheet(null)} />

      {update && (
        <div class="toast" role="status">
          <span class="grow">A new version of Puck Tech is ready.</span>
          <button type="button" onClick={update}>Reload</button>
        </div>
      )}
    </>
  );
}
